import * as THREE from 'three';

// The moon's glare: a soft glow drawn round the moon, in the camera-locked sky with the sun and the stars.
//
// The moon itself, the lit sphere with its surface map, is painted by the far shell (farShell.ts) like every other body that is too far to be geometry: from the registry's position
// of it relative to the observer, so it is right from the ground, from half way and from beside it (the old disc was the server's direction for the world, drawn at the sky radius).
// What stays here is the glare, a sprite that is not a body: light scattered in the eye and the air round a bright source. It scales with the light the moon delivers (its beam at the
// eye), not with its disc's surface brightness, so a full moon at night glows clearly, a crescent faintly, and by day not at all; it is tinted by the beam's colour (a low moon glows
// orange) and hidden by cloud in its own direction (the sky's probe) the way the sun's glare is.
//
// The beam. It is the moon's light outside the air (the registry's: the disc's mean brightness times its solid angle) times the air's transmittance along its own direction from the eye's
// height (the atmosphere's tables, eyeLight.ts), so it is the same on the ground, on a mountain and out of the air. The cloud in front of it is the sky's probe, per pixel, in the shader.
//
// From close the moon is a sphere and not a point of light: the glare fades as its angular radius grows past GLARE_ANG_LO and is gone by GLARE_ANG_HI.

export const MOON_GLARE_RADIUS = 12;     // glare plane half-width, in moon radii (as the sun's is)
export const MOON_GLARE_STRENGTH = 0.35; // peak opacity of the glare at a bright moon; 0 turns it off
export const MOON_CLOUD_LO = 0.30;       // probe transmittance at and below which cloud hides the glare completely ...
export const MOON_CLOUD_HI = 0.90;       // ... and at and above which it does not touch it (as sunCloudT)
/** The drawn size of the moon relative to its true angular size, for a moon that is a few pixels across (the sky's choice, as before); it goes to 1 as the moon grows. Used by the far shell. */
export const MOON_SIZE_SCALE = 2.0;
export const GLARE_ANG_LO = 0.6 * Math.PI / 180;
export const GLARE_ANG_HI = 3.0 * Math.PI / 180;
const SKY_EXPOSURE = 6.0;                // must match the renderer's skylight -> colour curve
const MOON_GLARE_KNEE = 0.02;            // x at which the glare is half strength (x = SKY_EXPOSURE * gain * beam luminance)

export interface MoonGlare {
  /** Unit direction from the eye to the moon, three's axes. */
  dir: ArrayLike<number>;
  /** Angular radius of the moon's true disc, radians. */
  angR: number;
  /** The moon's beam at the eye, per channel (the sky's units, after whatever air is in the way). */
  beam: ArrayLike<number>;
  /** Something solid (the planet) is in front of it. */
  hidden: boolean;
}

export class MoonView {
  readonly halo: THREE.Mesh;
  private haloGeo = new THREE.PlaneGeometry(2, 2);
  private haloMat: THREE.ShaderMaterial;

  constructor(private skyRadius: number) {
    this.haloMat = new THREE.ShaderMaterial({
      vertexShader: 'varying vec2 vUv;\nvarying vec3 vSky;\nvoid main() { vUv = uv; vSky = (modelMatrix * vec4(position, 1.0)).xyz - cameraPosition; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: `
        uniform vec3 uColor; uniform float uOpacity; uniform float uSrgbOut; uniform samplerCube uCloudT; uniform float uCloudOn; uniform vec2 uCloudCut; varying vec2 vUv; varying vec3 vSky;
        vec3 encodeSrgb(vec3 c) { return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c)); }
        void main() {
          // the same profile as the sun's glare (sunGlare.ts): a tight core falling roughly as an inverse
          // square, a faint wide veil, and nothing at the rim. r = 1 / MOON_GLARE_RADIUS is the disc's edge.
          float r = length(vUv * 2.0 - 1.0);
          float core = 1.0 / (1.0 + pow(r / 0.11, 2.0));
          float veil = exp(-pow(r / 0.42, 2.0));
          float ct = smoothstep(uCloudCut.x, uCloudCut.y, textureCube(uCloudT, normalize(vSky)).a);   // glare shows in gaps and through thin veil, not through cloud
          float a = uOpacity * (0.55 * core + 0.30 * veil) * (1.0 - smoothstep(0.6, 1.0, r)) * mix(1.0, ct, uCloudOn);
          vec3 c = uSrgbOut > 0.5 ? encodeSrgb(uColor) : uColor;
          gl_FragColor = vec4(c, a);
        }`,
      uniforms: { uColor: { value: new THREE.Color(1, 1, 1) }, uOpacity: { value: 0 }, uSrgbOut: { value: 1 }, uCloudT: { value: null as THREE.Texture | null }, uCloudOn: { value: 0 }, uCloudCut: { value: new THREE.Vector2(MOON_CLOUD_LO, MOON_CLOUD_HI) } },
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    this.halo = new THREE.Mesh(this.haloGeo, this.haloMat);
    this.halo.frustumCulled = false;
    this.halo.renderOrder = -5;
    this.halo.visible = false;
  }

  /** The sky's probe cube (Sky.probe.texture), or null before it has been drawn: hides the glare behind cloud. */
  setCloudProbe(tex: THREE.Texture | null) {
    this.haloMat.uniforms.uCloudT.value = tex;
    this.haloMat.uniforms.uCloudOn.value = tex ? 1 : 0;
  }

  /** Per frame. `g` null hides the glare. `gain` is the display exposure gain. */
  update(cameraPos: THREE.Vector3, g: MoonGlare | null, gain: number, srgbOut: boolean) {
    if (!g || g.hidden || !(g.angR > 0)) { this.halo.visible = false; return; }
    const tx = g.dir[0], ty = g.dir[1], tz = g.dir[2];
    const R = this.skyRadius * Math.tan(g.angR) * MOON_SIZE_SCALE;
    this.halo.position.set(cameraPos.x + tx * this.skyRadius, cameraPos.y + ty * this.skyRadius, cameraPos.z + tz * this.skyRadius);
    this.halo.scale.setScalar(R * MOON_GLARE_RADIUS);
    this.halo.lookAt(cameraPos);
    // Glare: a glow whose strength follows how bright the moon's beam is on screen, tinted by the beam colour. Scatter in the eye and the air scales with the light received, not the
    // disc's own surface brightness, so this uses the beam irradiance.
    const m = Math.max(g.beam[0], g.beam[1], g.beam[2]);
    const lumBeam = 0.2126 * g.beam[0] + 0.7152 * g.beam[1] + 0.0722 * g.beam[2];
    const hu = this.haloMat.uniforms;
    if (m > 0) (hu.uColor.value as THREE.Color).setRGB(g.beam[0] / m, g.beam[1] / m, g.beam[2] / m);
    const x = SKY_EXPOSURE * gain * lumBeam;
    const grow = 1 - THREE.MathUtils.smoothstep(g.angR, GLARE_ANG_LO, GLARE_ANG_HI);
    hu.uOpacity.value = MOON_GLARE_STRENGTH * (x / (x + MOON_GLARE_KNEE)) * grow;
    hu.uSrgbOut.value = srgbOut ? 1 : 0;
    this.halo.visible = hu.uOpacity.value > 0.003;
  }

  dispose() {
    this.haloGeo.dispose();
    this.haloMat.dispose();
  }
}
